import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Paper } from "@mui/material";
import ProjectHelpHeader from "./ProjectHelpHeader";
import ProjectHelpBodyCompany from "./companyUser/ProjectHelpBodyCompany";
import ProjectHelpBodyUser from "./user/ProjectHelpBodyUser";

/**
 * サジェストウィジェット（desktop専用）
 *
 * - 画面内位置の計算
 * - anchorRef による初期スナップ
 * - ドラッグ移動
 * - resize 追従
 *
 * ※ mobile ではこのコンポーネント自体が描画されない
 */
export default function ProjectHelpWidgetDesktop({
  mode,
  step,
  formState,
  anchorRef,
  open,
  setOpen,
  isDesktop,
}) {
  // ウィジェットサイズ・マージン
  const W = 340;
  const M = 24;
  const ANCHOR_OFFSET_Y = -20;

  // 値を画面内に収めるユーティリティ
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  // 表示位置
  const [pos, setPos] = useState({ x: 0, y: 0 });

  // ドラッグ状態
  const [dragging, setDragging] = useState(false);

  // ドラッグ開始時の差分
  const dragOffsetRef = useRef({ dx: 0, dy: 0 });

  // ユーザーが一度でも動かしたか（以後スナップしない）
  const userMovedRef = useRef(false);

  // 初回スナップ済みか（StrictModeのuseEffect二重実行対策）
  const snappedOnceRef = useRef(false);

  /**
   * 位置の初期スナップと画面内クランプ処理
   * - 初回のみアンカー基準で配置
   * - open変更・resize時は未ドラッグなら再クランプ
   */
  useEffect(() => {
    const getEstimatedHeight = () => (open ? 450 : 56);

    const init = () => {
      if (userMovedRef.current) return;

      const estimatedHeight = getEstimatedHeight();

      const maxX = window.innerWidth - W - M;
      const maxY = window.innerHeight - estimatedHeight - M;

      // 初回だけアンカー基準でスナップ
      if (!snappedOnceRef.current) {
        const rect = anchorRef?.current?.getBoundingClientRect?.();

        const anchorBottom = rect?.bottom ?? null;
        const yByAnchor =
          anchorBottom != null ? anchorBottom + ANCHOR_OFFSET_Y : null;

        let x = window.innerWidth - W - M;
        let y = yByAnchor ?? window.innerHeight - estimatedHeight - M;

        x = clamp(x, M, Math.max(M, maxX));
        y = clamp(y, M, Math.max(M, maxY));

        setPos({ x, y });
        snappedOnceRef.current = true;
        return;
      }

      // 2回目以降は「現在位置」を画面内に再調整
      setPos((prev) => ({
        x: clamp(prev.x, M, Math.max(M, maxX)),
        y: clamp(prev.y, M, Math.max(M, maxY)),
      }));
    };

    init();
    window.addEventListener("resize", init);
    return () => window.removeEventListener("resize", init);
  }, [anchorRef, open]);

  // ヘッダー押下時：ドラッグ開始
  const onPointerDownHeader = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);

    const rect = e.currentTarget
      .closest("[data-widget-root]")
      ?.getBoundingClientRect();
    if (!rect) return;

    dragOffsetRef.current = {
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
    };

    setDragging(true);
  };

  // ドラッグ中の位置更新
  const onPointerMove = (e) => {
    if (!dragging) return;

    userMovedRef.current = true;

    const nextX = e.clientX - dragOffsetRef.current.dx;
    const nextY = e.clientY - dragOffsetRef.current.dy;

    const maxX = window.innerWidth - W - M;
    const maxY = window.innerHeight - 80 - M;

    setPos({
      x: clamp(nextX, M, Math.max(M, maxX)),
      y: clamp(nextY, M, Math.max(M, maxY)),
    });
  };

  // ドラッグ終了
  const onPointerUp = (e) => {
    if (!dragging) return;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    }
    setDragging(false);
  };

  // 開閉トグル
  const toggleOpen = () => setOpen((v) => !v);

  /**
   * mode に応じて表示する Body を切り替える
   * - user：ProjectHelpBodyUser
   * - company：ProjectHelpBodyCompany
   */
  const { Body, bodyProps } = useMemo(() => {
    if (mode === "user") {
      return {
        Body: ProjectHelpBodyUser,
        bodyProps: { step, formState },
      };
    }

    return {
      Body: ProjectHelpBodyCompany,
      bodyProps: { formState },
    };
  }, [mode, step, formState]);

  return (
    <Box
      data-widget-root
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: W,
        zIndex: 1200,
        cursor: dragging ? "grabbing" : "default",
      }}
    >
      <Paper elevation={6} sx={{ borderRadius: 2, overflow: "hidden" }}>
        {/* ヘッダー（開閉・ドラッグ起点） */}
        <ProjectHelpHeader
          open={open}
          dragging={dragging}
          onToggle={toggleOpen}
          onPointerDown={onPointerDownHeader}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerMove={onPointerMove}
          isDesktop={isDesktop}
        />

        {/* 本文（開いているときのみ） */}
        {open && <Body {...bodyProps} />}
      </Paper>
    </Box>
  );
}
